import { Component, OnInit } from '@angular/core';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { UserPlatformService } from '../../../../core/services/user-platform/user-platform.service';
import { ModalService } from '../../../../core/services/modal/modal.service';
import { PermissionService } from '../../../../core/services/permission/permission.service';
import { environment } from '../../../../../environments/environment';

interface UserRow {
  user_id: string;
  name: string;
  image: string;
  role: string;
  platform_ids: string[];
  pending_ids: string[];
}

interface ChannelGroup {
  platform: string;
  channels: any[];
}

const GROUP_PREFIX = '__group__';

@Component({
  selector: 'app-setting-user-platform',
  templateUrl: './setting-user-platform.component.html',
  styleUrl: './setting-user-platform.component.css'
})
export class SettingUserPlatformComponent implements OnInit {

  channels: any[] = [];
  channelGroups: ChannelGroup[] = [];
  userDatasets: UserRow[] = [];
  saving: { [user_id: string]: boolean } = {};
  loading = false;
  system_id = environment.systemId;

  selectedUserIds = new Set<string>();
  bulkPendingIds: string[] = [];
  bulkSaving = false;

  constructor(
    private authService: AuthService,
    private platformService: PlatformService,
    private userPlatformService: UserPlatformService,
    private modalService: ModalService,
    public permissionService: PermissionService,
  ) { }

  async ngOnInit(): Promise<void> {
    await this.loadDatasets();
  }

  async loadDatasets() {
    this.loading = true;
    try {
      const [userInfo, mappings, platforms]: any = await Promise.all([
        this.authService.getAllUserInfo(),
        this.userPlatformService.getAll(),
        this.platformService.getAllPlatform({}),
      ]);

      const infos = userInfo?.success ? userInfo.data : [];
      const maps = mappings?.success ? mappings.data : [];
      this.channels = platforms?.success ? platforms.data : [];
      this.channelGroups = this.buildChannelGroups(this.channels);

      this.userDatasets = infos.map((info: any) => {
        const lineInfo = info?.channels?.find((e: any) => e.type === 'line')?.info || {};
        const role = info?.permissions?.find((e: any) => e.active && e.selected && e.system_id === this.system_id) || {};
        const mapping = maps.find((m: any) => m.user_id === info.user_id);
        const platformIds = mapping?.platform_ids || [];
        return {
          user_id: info.user_id,
          name: lineInfo?.displayName || info?.name || info.user_id,
          image: lineInfo?.pictureUrl || '',
          role: role?.role || '',
          platform_ids: platformIds,
          pending_ids: [...platformIds]
        };
      });
    } finally {
      this.loading = false;
    }
  }

  hasChanges(user: UserRow): boolean {
    if (user.platform_ids.length !== user.pending_ids.length) return true;
    const current = new Set(user.platform_ids);
    return user.pending_ids.some(id => !current.has(id));
  }

  resetChanges(user: UserRow) {
    user.pending_ids = [...user.platform_ids];
  }

  async savePlatforms(user: UserRow) {
    if (this.saving[user.user_id]) return;
    if (!this.hasChanges(user)) return;
    this.saving[user.user_id] = true;
    try {
      const res: any = await this.userPlatformService.update(user.user_id, user.pending_ids);
      if (res?.success) {
        user.platform_ids = [...user.pending_ids];
        this.modalService.openModal('success', 'สำเร็จ', 'อัพเดต scope ช่องทางสำเร็จ');
      } else {
        this.modalService.openModal('fail', 'ไม่สำเร็จ', 'ไม่สามารถอัพเดต scope ช่องทางได้');
      }
    } finally {
      this.saving[user.user_id] = false;
    }
  }

  getSelectedChannelsInGroup(user: UserRow, group: ChannelGroup): any[] {
    return group.channels.filter(c => user.platform_ids.includes(c.platform_id));
  }

  totalSelected(user: UserRow): number {
    return user.platform_ids.length;
  }

  totalChannels(): number {
    return this.channels.length;
  }

  private buildChannelGroups(channels: any[]): ChannelGroup[] {
    const map = new Map<string, any[]>();
    for (const ch of channels) {
      const key = ch.platform || 'อื่นๆ';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ch);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([platform, list]) => ({ platform, channels: list }));
  }

  groupSelectedCount(group: ChannelGroup, ids: string[]): number {
    return group.channels.filter(c => ids.includes(c.platform_id)).length;
  }

  groupMarkerValue(group: ChannelGroup): string {
    return GROUP_PREFIX + group.platform;
  }

  private resolveSelectionChange(currentIds: string[], newValues: string[]): string[] {
    const groupMarkers = newValues.filter(v => v.startsWith(GROUP_PREFIX));
    if (groupMarkers.length === 0) return newValues;

    let result = newValues.filter(v => !v.startsWith(GROUP_PREFIX));
    for (const marker of groupMarkers) {
      const platformName = marker.slice(GROUP_PREFIX.length);
      const group = this.channelGroups.find(g => g.platform === platformName);
      if (!group) continue;
      const groupIds = group.channels.map(c => c.platform_id);
      const wasAllSelected = groupIds.every(id => currentIds.includes(id));
      if (wasAllSelected) {
        result = result.filter(id => !groupIds.includes(id));
      } else {
        result = Array.from(new Set([...result, ...groupIds]));
      }
    }
    return result;
  }

  onSelectionChange(user: UserRow, values: string[]) {
    user.pending_ids = this.resolveSelectionChange(user.pending_ids, values);
  }

  onBulkSelectionChange(values: string[]) {
    this.bulkPendingIds = this.resolveSelectionChange(this.bulkPendingIds, values);
  }

  isUserSelected(userId: string): boolean {
    return this.selectedUserIds.has(userId);
  }

  toggleUserSelected(userId: string) {
    if (this.selectedUserIds.has(userId)) {
      this.selectedUserIds.delete(userId);
    } else {
      this.selectedUserIds.add(userId);
    }
  }

  isAllUsersSelected(): boolean {
    return this.userDatasets.length > 0 && this.selectedUserIds.size === this.userDatasets.length;
  }

  isSomeUsersSelected(): boolean {
    return this.selectedUserIds.size > 0 && !this.isAllUsersSelected();
  }

  toggleAllUsers() {
    if (this.isAllUsersSelected()) {
      this.selectedUserIds.clear();
    } else {
      this.selectedUserIds = new Set(this.userDatasets.map(u => u.user_id));
    }
  }

  clearBulkSelection() {
    this.selectedUserIds.clear();
    this.bulkPendingIds = [];
  }

  async applyBulk() {
    const userIds = Array.from(this.selectedUserIds);
    if (userIds.length === 0 || this.bulkSaving) return;

    const platformLabel = this.bulkPendingIds.length === 0
      ? 'ไม่มี platform (ลบสิทธิ์ทั้งหมด)'
      : `${this.bulkPendingIds.length} platform`;
    const confirmed = await this.modalService.openModal(
      'confirm',
      'ยืนยันการตั้งค่า',
      `กำหนด ${platformLabel} ให้ผู้ใช้ ${userIds.length} คน?`
    );
    if (!confirmed) return;

    this.bulkSaving = true;
    let successCount = 0;
    try {
      const results = await Promise.all(
        userIds.map(uid => this.userPlatformService.update(uid, this.bulkPendingIds))
      );
      results.forEach((res: any, i) => {
        if (res?.success) {
          successCount++;
          const user = this.userDatasets.find(u => u.user_id === userIds[i]);
          if (user) {
            user.platform_ids = [...this.bulkPendingIds];
            user.pending_ids = [...this.bulkPendingIds];
          }
        }
      });

      if (successCount === userIds.length) {
        this.modalService.openModal('success', 'สำเร็จ', `อัพเดต ${successCount} user สำเร็จ`);
      } else {
        this.modalService.openModal('fail', 'บางส่วนไม่สำเร็จ', `สำเร็จ ${successCount}/${userIds.length}`);
      }
      this.clearBulkSelection();
    } finally {
      this.bulkSaving = false;
    }
  }
}
